import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { AxiosResponse } from 'axios';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import {
  PatchPostCategoryParam,
  PinnedLaningRequest,
  PinnedExplorePostRequest,
  PostManagementParam,
  UpdatePostDataParam,
} from '@/Interfaces/I_postManagement';

import {
  createSponsoredPost,
  deleteSponsoredPost,
  updateSponsoredPost,
  getCategories,
  getMallCategories,
  getPinnedLandings,
  getPinnedExplorePosts,
  getPost,
  getSponsoredPosts,
  patchPostCategory,
  patchPostCategorySuccess,
  patchPostData,
  patchPostDataSuccess,
  reorderSponsoredPosts,
  setCategories,
  setKeywordPost,
  setMallCategories,
  setPinnedLandings,
  setSponsoredPosts,
  updatePinnedLandings,
  setPinnedExplorePosts,
  setPost,
  updatePinnedExplorePosts,
} from '@/Slices/PostManagementSlice';

import {
  createSponsoredPostAPI,
  deleteSponsoredPostAPI,
  updateSponsoredPostAPI,
  fetchCategoriesAPI,
  fetchMallCategoriesAPI,
  fetchPinnedLandingAPI,
  fetchPinnedExplorePostsAPI,
  fetchPostAPI,
  fetchSponsoredPostsAPI,
  patchPostCategoryAPI,
  patchPostDataAPI,
  reorderSponsoredPostsAPI,
  updatePinnedLandingsAPI,
  updatePinnedExplorePostsAPI,
} from '@/Axios/api/postManagement';

function* handleGetPost(action: PayloadAction<PostManagementParam>) {
  try {
    const data: AxiosResponse = yield call(fetchPostAPI, action.payload.params);
    yield put(action.payload.pageName === 'keyword' ? setKeywordPost(data) : setPost(data));
  } catch (e) {
    // console.log(e);
  }
}

function* handleGetCategories() {
  try {
    const data: AxiosResponse = yield call(fetchCategoriesAPI);

    yield put(setCategories(data));
  } catch (e) {
    // console.log(e);
  }
}

function* handlePatchPostData(action: PayloadAction<UpdatePostDataParam[]>) {
  try {
    const data: AxiosResponse = yield call(patchPostDataAPI, action.payload);
    yield put(patchPostDataSuccess(data.data[0]));
    message.success({
      content: 'Post(s) updated.',
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
  } catch (e) {
    message.error({
      content: 'Fail to update Post(s).',
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
  }
}

function* handlePatchPostCategory(action: PayloadAction<PatchPostCategoryParam>) {
  try {
    yield call(patchPostCategoryAPI, action.payload);
    yield put(patchPostCategorySuccess({}));
    message.success({
      content: 'Post category updated.',
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
  } catch (e) {
    message.error({
      content: 'Fail to update, please try again.',
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
  }
}

function* handleGetMallCategories() {
  try {
    const data: AxiosResponse = yield call(fetchMallCategoriesAPI);

    yield put(setMallCategories(data));
  } catch (e) {
    // console.log(e);
  }
}

function* handleGetPinnedExplorePosts(action: PayloadAction<string>) {
  try {
    const data: AxiosResponse = yield call(fetchPinnedExplorePostsAPI, action.payload);
    yield put(setPinnedExplorePosts(data));
  } catch (e) {
    // console.log(e);
  }
}

function* handleGetPinnedLanding(action: PayloadAction<string>) {
  try {
    const data: AxiosResponse = yield call(fetchPinnedLandingAPI, action.payload);
    yield put(setPinnedLandings(data));
  } catch (e) {
    // console.log(e);
  }
}

function* handleUpdatePinnedLandings(action: PayloadAction<PinnedLaningRequest>) {
  try {
    const data: AxiosResponse = yield call(updatePinnedLandingsAPI, action.payload);
    yield put(setPinnedLandings(data));
  } catch (e) {
    // console.log(e);
  }
}

function* handleUpdatePinnedExplorePosts(action: PayloadAction<PinnedExplorePostRequest>) {
  try {
    const data: AxiosResponse = yield call(updatePinnedExplorePostsAPI, action.payload);
    yield put(setPinnedExplorePosts(data));
  } catch (e) {
    // console.log(e);
  }
}

function* handleGetSponsoredPosts() {
  try {
    const data: AxiosResponse = yield call(fetchSponsoredPostsAPI);
    yield put(setSponsoredPosts(data));
  } catch (e) {
    // console.log(e);
  }
}

function* handleCreateSponsoredPost(action: PayloadAction<{ postUuid: string; isAds?: boolean }>) {
  try {
    const createResponse: AxiosResponse = yield call(createSponsoredPostAPI, action.payload);
    const newItem = createResponse.data;
    const state: { postManagement: { value: { sponsoredPosts: unknown[] } } } = yield select();
    const currentList = state.postManagement?.value?.sponsoredPosts ?? [];
    const updatedList = [...currentList, newItem];
    yield put(setSponsoredPosts({ data: updatedList }));
    message.success({ content: 'Sponsored post added.', duration: 3, style: { marginTop: '120px' } });
  } catch (e) {
    message.error({ content: 'Failed to add sponsored post.', duration: 3, style: { marginTop: '120px' } });
  }
}

function* handleReorderSponsoredPosts(action: PayloadAction<{ items: { id: number; seq: number }[] }>) {
  try {
    const data: AxiosResponse = yield call(reorderSponsoredPostsAPI, action.payload);
    yield put(setSponsoredPosts(data));
    message.success({ content: 'Order updated.', duration: 3, style: { marginTop: '120px' } });
  } catch (e) {
    message.error({ content: 'Failed to reorder.', duration: 3, style: { marginTop: '120px' } });
  }
}

function* handleUpdateSponsoredPost(action: PayloadAction<{ id: number; isAds: boolean }>) {
  const { id, isAds } = action.payload;
  const state: { postManagement: { value: { sponsoredPosts: { id: number }[] } } } = yield select();
  const currentList = state.postManagement?.value?.sponsoredPosts ?? [];

  try {
    yield call(updateSponsoredPostAPI, id, { isAds });
    const updatedList = currentList.map((item) =>
      item.id === id ? { ...item, isAds } : item,
    );
    yield put(setSponsoredPosts({ data: updatedList }));
    message.success({ content: 'Ads status updated.', duration: 3, style: { marginTop: '120px' } });
  } catch (e) {
    const data: AxiosResponse = yield call(fetchSponsoredPostsAPI);
    yield put(setSponsoredPosts(data));
    message.error({ content: 'Failed to update ads status.', duration: 3, style: { marginTop: '120px' } });
  }
}

function* handleDeleteSponsoredPost(action: PayloadAction<number>) {
  const idToRemove = action.payload;
  const state: { postManagement: { value: { sponsoredPosts: { id: number }[] } } } = yield select();
  const currentList = state.postManagement?.value?.sponsoredPosts ?? [];

  try {
    yield call(deleteSponsoredPostAPI, idToRemove);
    const updatedList = currentList.filter((item) => item.id !== idToRemove);
    yield put(setSponsoredPosts({ data: updatedList }));
    message.success({ content: 'Sponsored post removed.', duration: 3, style: { marginTop: '120px' } });
  } catch (e) {
    const data: AxiosResponse = yield call(fetchSponsoredPostsAPI);
    yield put(setSponsoredPosts(data));
    message.error({ content: 'Failed to remove sponsored post.', duration: 3, style: { marginTop: '120px' } });
  }
}

export function* watchGetPost() {
  yield takeLatest(getPost.type, handleGetPost);
}

export function* watchGetCategories() {
  yield takeLatest(getCategories.type, handleGetCategories);
}

export function* watchPatchExtraScore() {
  yield takeEvery(patchPostData.type, handlePatchPostData);
}

export function* watchPatchPostCategory() {
  yield takeLatest(patchPostCategory.type, handlePatchPostCategory);
}

export function* watchGetMallCategories() {
  yield takeLatest(getMallCategories.type, handleGetMallCategories);
}

export function* watchGetPinnedLanding() {
  yield takeLatest(getPinnedLandings.type, handleGetPinnedLanding);
}

export function* watchUpdatePinnedLandings() {
  yield takeLatest(updatePinnedLandings.type, handleUpdatePinnedLandings);
}

export function* watchGetPinnedExplorePosts() {
  yield takeLatest(getPinnedExplorePosts.type, handleGetPinnedExplorePosts);
}

export function* watchUpdatePinnedExplorePosts() {
  yield takeLatest(updatePinnedExplorePosts.type, handleUpdatePinnedExplorePosts);
}

export function* watchGetSponsoredPosts() {
  yield takeLatest(getSponsoredPosts.type, handleGetSponsoredPosts);
}

export function* watchCreateSponsoredPost() {
  yield takeEvery(createSponsoredPost.type, handleCreateSponsoredPost);
}

export function* watchReorderSponsoredPosts() {
  yield takeEvery(reorderSponsoredPosts.type, handleReorderSponsoredPosts);
}

export function* watchDeleteSponsoredPost() {
  yield takeEvery(deleteSponsoredPost.type, handleDeleteSponsoredPost);
}

export function* watchUpdateSponsoredPost() {
  yield takeEvery(updateSponsoredPost.type, handleUpdateSponsoredPost);
}
